import _objectSpread from "@babel/runtime/helpers/objectSpread";
import makeHttpAction from '../../actions/makeHttpAction';
import makePlainAction from '../../actions/makePlainAction';
import capitalize from '../../../utils/capitalize'; // Object representation of the default actions. This is processed in the
// makeModelActions function to create the actual set of actions

var DEFAULT_ACTIONS = [{
  verb: "get",
  method: "get"
}, {
  verb: "get",
  method: "get",
  batch: true
}, {
  verb: "create",
  method: "post"
}, {
  verb: "create",
  method: "post",
  batch: true
}, {
  verb: "update",
  method: "put"
}, {
  verb: "update",
  method: "put",
  batch: true
}, {
  verb: "destroy",
  method: "delete"
}, {
  verb: "select",
  notHttp: true
}, {
  verb: "deselect",
  notHttp: true
}];
/**
 * Creates a set of actions that will handle all common CRUD actions for the
 * given model. Assuming the model was `foo`, the created actions would be:
 *
 * * `getFoo`
 * * `getFoos`
 * * `createFoo`
 * * `createFoos`
 * * `updateFoo`
 * * `updateFoos`
 * * `destroyFoo` **NOTE:** `destroyFoos` is not created for security reasons.
 * * `selectFoo`
 * * `deselectFoo`
 *
 * The `<verb><Model>Start`, `<verb><Model>Success`, and `<verb><Model>Failure`
 * actions are also included but are dispatched automatically.
 *
 * `makeModelActions` also includes synchronous versions of all HTTP actions. For
 * example, `getFooSync` is a synchronous version of `getFoo`. It is almost a
 * direct clone of the `getFooSuccess` action. NOTE: synchronous actions do not
 * add `_SYNC` to their type. Ie. `getFooSync` has the type `GET_FOO`, not
 * `GET_FOO_SYNC`.
 *
 * @param {Object} options The options for the creator
 * @param {string} options.model The name of the model
 * @param {string} options.pluralModel The plural name of the model. Defaults to the model with an 's'.
 * @param {string} options.baseUrl The API url to make http requests to
 * @param {string} options.endpoint The API endpoint to make http requests to
 * @param {Array} options.exclude A list of actions to avoid generating
 * @param {Array} options.include A list of actions to exclusively generate
 * @return {Object} The set of all model actions
 *
 * @public
 * @author Connor Taylor
 * @example
 *
 * const baseArgs = {
 *   model: `foo`,
 *   baseUrl: `http://api.foo.com`,
 *   endpoint: `foo`, // See makeHttpAction for more details about the endpoint param
 * }
 *
 * // Will generate all default actions
 * let actions = makeModelActions(baseArgs);
 *
 * // Use the `exclude` parameter to prevent an action from being generated
 * // This set will have all default actions except for `selectFoo`
 * actions = makeModelActions({ ...baseArgs, exclude: [`selectFoo`] });
 *
 * // Use the `include` parameter when you want to only generate the specified
 * // actions. This set will only have the `selectFoo` action.
 * actions = makeModelActions({ ...baseArgs, include: [`selectFoo`] });
 *
 * // Use the `pluralModel` parameter when you want to use a different plural
 * // version of the model. This defaults to `${model}s`.
 * actions = makeModelActions({ model: `bary`, pluralModel: `baries` });
 */

var makeModelActions = function makeModelActions(_ref) {
  var model = _ref.model,
      baseUrl = _ref.baseUrl,
      _ref$pluralModel = _ref.pluralModel,
      pluralModel = _ref$pluralModel === void 0 ? null : _ref$pluralModel,
      _ref$endpoint = _ref.endpoint,
      endpoint = _ref$endpoint === void 0 ? "" : _ref$endpoint,
      _ref$exclude = _ref.exclude,
      exclude = _ref$exclude === void 0 ? [] : _ref$exclude,
      _ref$include = _ref.include,
      include = _ref$include === void 0 ? [] : _ref$include;
  var titleModel = capitalize(model);
  var pluralVersion = pluralModel || model + "s";
  var titlePluralModel = capitalize(pluralVersion); // Adds actionKey to each action object

  var format = function format(accum, a) {
    var actionKey = "" + a.verb + (a.batch ? titlePluralModel : titleModel);
    var syncActionKey = actionKey + "Sync"; // notHttp actions don't need a synchronous version available since they
    // are already synchronous

    return a.notHttp ? accum.concat([_objectSpread({}, a, {
      actionKey: actionKey
    })]) : accum.concat([_objectSpread({}, a, {
      actionKey: actionKey
    }), _objectSpread({}, a, {
      actionKey: syncActionKey,
      notHttp: true
    })]);
  }; // Filter out any actions based on include / exclude values


  var filter = function filter(accum, a) {
    var notIncluded = function notIncluded() {
      return include.length !== 0 && !include.includes(a.actionKey);
    };

    var excluded = function excluded() {
      return exclude.length !== 0 && exclude.includes(a.actionKey);
    };

    return notIncluded() || excluded() ? accum : accum.concat([a]);
  };

  return DEFAULT_ACTIONS.reduce(format, []).reduce(filter, []).reduce(function (accum, a) {
    var _objectSpread2;

    return _objectSpread({}, accum, (_objectSpread2 = {}, _objectSpread2[a.actionKey] = a.notHttp ? makePlainAction({
      model: a.batch ? pluralVersion : model,
      verb: a.verb
    }) : makeHttpAction(_objectSpread({}, a, {
      model: model,
      pluralModel: pluralVersion,
      baseUrl: baseUrl,
      endpoint: endpoint
    })), _objectSpread2));
  }, {});
};
/** @component */


export default makeModelActions;